import { useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { useOnClickOutside } from '@shared/hook';
import { HelpersUtils } from '@shared/utils';
import { RangePicker } from './rangePicker';
import { ButtonV2 } from '@shared/widgets';
import CloseIcon from '@assets/icons/clear.svg';
import CalendarIcon from '@assets/icons/calendar.svg';

const DATE_FORMAT = 'DD.MM.YYYY';

export interface IDate {
  from: Date | null;
  to: Date | null;
}

interface IDatePickerProps {
  className?: string;
  range: IDate;
  disablePast?: boolean;
  disableFuture?: boolean;
  onConfirm: (date: IDate) => void;
}

export const DatePicker = (props: IDatePickerProps) => {
  const [open, setOpen] = useState<boolean>(false);
  const [confirmed, setConfirmed] = useState<boolean>(false);
  const [selectedDays, setSelectedDays] = useState<IDate>(props.range);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && dropdownRef.current) {
      const dropdown = dropdownRef.current;
      const rect = dropdown.getBoundingClientRect();
      const visibleBottom = window.innerHeight;
      const visibleRight = window.innerWidth;
      const offsetTop = rect.bottom - visibleBottom;
      const offsetLeft = rect.right - visibleRight;

      const computedStyles = window.getComputedStyle(dropdown);
      const currentTop = computedStyles.top;
      const currentLeft = computedStyles.left;

      if (offsetTop > 0) dropdown.style.top = `calc(${currentTop} - ${offsetTop}px)`;
      if (offsetLeft > 0) dropdown.style.left = `calc(${currentLeft} - ${offsetLeft}px)`;
    }
  }, [open]);

  useEffect(() => {
    setSelectedDays(props.range);
  }, [props.range]);

  const handleCloseClick = () => {
    close();
    clearSelected();
  };
  useOnClickOutside(dropdownRef as React.RefObject<HTMLDivElement>, handleCloseClick);

  const handleDayClick = (days: IDate) => {
    setSelectedDays(days);
    setConfirmed(false);
  };

  const handleCaptionClick = () => {
    if (open) return;
    if (screen.width < 768) HelpersUtils.appScroll.disable();
    setOpen(true);
  };

  const handleResetClick = (e: React.MouseEvent<SVGSVGElement>) => {
    e.stopPropagation();
    clearSelected();
    props.onConfirm({ from: null, to: null });
    setConfirmed(false);
  };

  const handleConfirmClick = () => {
    props.onConfirm(selectedDays);
    close();
    setConfirmed(true);
  };
  const close = () => {
    if (!open) return;
    if (screen.width < 768) HelpersUtils.appScroll.enable();
    setOpen(false);
  };
  const clearSelected = () => {
    if (!confirmed) setSelectedDays(props.range || null);
  };

  const renderCaptionText = (data?: IDate | null) => {
    let text = '';

    if (data) {
      text += data?.from ? dayjs(data.from).format(DATE_FORMAT) : '';
      text += data?.from && data.to ? ' - ' : '';
      text += data?.to ? dayjs(data.to).format(DATE_FORMAT) : '';
    }
    if (!text.length) text = 'Click to pick date range';

    return <div className='flex-1 pr-5 text-sm font-semibold text-[#fff] uppercase opacity-90'>{text}</div>;
  };

  return (
    <div
      ref={datePickerRef}
      className={clsx(
        'relative inline-flex h-[50px] min-w-[295px] cursor-pointer items-center justify-end bg-[linear-gradient(180deg,#4a4a4a,#3e3e3e)] pl-5 whitespace-nowrap shadow-[2px_2px_4px_0_#0000003b,inset_0_1px_0_0_#595959] hover:bg-[linear-gradient(180deg,#3b3b3b,#373737)] hover:shadow-[2px_2px_4px_0_#0000003b,inset_0_1px_0_0_#4b4b4b] max-md:min-w-[240px]',
        props.className,
      )}
      onClick={handleCaptionClick}
    >
      {renderCaptionText(selectedDays)}
      {(props.range.from || props.range.to) && !open && <CloseIcon className='mx-2.5 text-[#f2f2f2]' onClick={handleResetClick} />}
      <div className={clsx('fixed top-0 right-0 bottom-0 left-0 z-[1500] hidden bg-[#191919b3]', open && '!block')} />
      <div className='relative h-[50px] w-[53px] p-4.5 pt-4 before:absolute before:top-0 before:left-0 before:h-[50px] before:w-[1px] before:bg-[#97815e] before:opacity-20'>
        <CalendarIcon className='text-[#f2f2f2]' />
      </div>
      {open && (
        <div
          ref={dropdownRef}
          className='absolute top-0 right-0 !z-[1500] w-full min-w-[290px] rounded-sm bg-[#2f3030] text-[#fff] shadow-[3px_3px_6px_0_#0000003b,inset_0_2px_0_0_#34343480] max-md:fixed max-md:top-1/2 max-md:left-1/2 max-md:max-w-[290px] max-md:-translate-1/2'
          onClick={(e) => e.stopPropagation()}
        >
          <div className='relative flex h-[50px] w-full cursor-pointer items-center justify-end px-5'>
            {renderCaptionText()}
            <CloseIcon className='mr-1 ml-2.5 text-[#f2f2f2]' onClick={handleCloseClick} />
          </div>
          <RangePicker onDayClick={handleDayClick} past={props.disablePast} future={props.disableFuture} range={props.range} />
          <div className='p-4 pt-0'>
            <ButtonV2 className='!h-[46px]' fullWidth disabled={!selectedDays || (props.range && !selectedDays.from)} onClick={handleConfirmClick}>
              Choose These Dates
            </ButtonV2>
          </div>
        </div>
      )}
    </div>
  );
};
